import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvVideoThumbnail from '../atoms/OvVideoThumbnail';
import OvRecordEditDialog from './OvRecordEditDialog';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { PosterFileRejection } from '../../../common/model/type/video-file.types';
import {
  posterContentType,
  validatePosterFile,
} from '../../../common/utils/video-file-utils';
import { useAdminVideoMutations } from '../../../hooks/use-admin-video-mutations';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvReplaceVideoPosterDialogProps {
  video: AdminVideo;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const OvReplaceVideoPosterDialog: FC<OvReplaceVideoPosterDialogProps> = ({
  video,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { replacePoster } = useAdminVideoMutations();
  const [file, setFile] = useState<File | null>(null);
  const [rejection, setRejection] = useState<PosterFileRejection | null>(null);

  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  );

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  const close = () => {
    setFile(null);
    setRejection(null);
    replacePoster.reset();
    onClose();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';

    if (!chosen) {
      return;
    }

    const nextRejection = validatePosterFile(chosen);
    setRejection(nextRejection);
    setFile(nextRejection ? null : chosen);
  };

  const handleSubmit = () => {
    const contentType = file && posterContentType(file);

    if (!file || !contentType) {
      return;
    }

    replacePoster.mutate(
      { id: video.id, file, contentType },
      {
        onSuccess: () => {
          close();
          onSaved();
        },
      }
    );
  };

  return (
    <OvRecordEditDialog
      isOpen={isOpen}
      title={t('videos.details.poster.title')}
      busyLabel={t('videos.details.poster.busy')}
      isDirty={!!file}
      isSaving={replacePoster.isPending}
      isSubmitDisabled={!file}
      errorMessage={
        replacePoster.isError ? t('videos.details.poster.failed') : undefined
      }
      showLiveNotice
      onSubmit={handleSubmit}
      onClose={close}
    >
      <OvVideoThumbnail
        src={previewUrl ?? video.poster_url}
        size="large"
        alt={t('videos.details.poster.previewAlt', { title: video.title })}
      />
      <Picker>
        <FileLabel>
          {t('videos.details.poster.choose')}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={replacePoster.isPending}
            onChange={handleChange}
          />
        </FileLabel>
        {file && <span>{file.name}</span>}
      </Picker>
      <Hint>{t('videos.details.poster.limits')}</Hint>
      {rejection && (
        <OvErrorMessage message={t(`videos.posterRejections.${rejection}`)} />
      )}
    </OvRecordEditDialog>
  );
};

export default OvReplaceVideoPosterDialog;

const Picker = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  overflow-wrap: anywhere;
`;

const FileLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 1rem;
  border: 1px solid ${Colours.OV_BASE};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
  cursor: pointer;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  &:focus-within {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const Hint = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
